/** The Profile tab of the user record (plan "Page anatomy"): the account band under the tabs
 *  (the page renders it above the ledger dock — `UserProfileBand`), then the person's panels
 *  in card order — Trips first, then the cards of the registry (`UserCardsView`): Contacts,
 *  Reachability, Identity, Access, Origin, Technical. Keys `1`–`7` jump to them.
 *
 *  The Trips panel and the band's Trips cell share one set of queries (`useProfileTrips`).
 *  They need `console_bookings_view` (the bookings list's own permission); and the list
 *  never returns a deleted user's bookings, so for a deleted user neither is shown rather
 *  than a false "No trips". Both are decided before anything is fetched, so the panel
 *  holds its place from the first paint (skeleton → trips, or "No bookings for this
 *  person"). The Trips panel is read-only; its rows lay out for the panel's own width in
 *  view mode (two lines on a laptop) and for its width beside the docked ledger in edit
 *  mode.
 *
 *  The cards need the editor's catalog and edit context: skeleton cards while they load,
 *  an error with Retry in their place when they fail (the rest of the page stays). */
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import type { UserRecordSection } from '@/api/userRecord';
import type { ConsoleUserDetail } from '@/api/users';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { spineColumns } from '@/features/records/views/spineColumns';
import { usePermissions } from '@/hooks/usePermissions';

import { AccountBand } from './AccountBand';
import { accountBandCells, type BandCell, type BandTab } from './accountBandModel';
import { UserCardsView, type CardTab } from './cards/UserCardsView';
import { focusAnchor } from './reveal';
import { TRIPS_ANCHOR_ID, TripsPanel } from './trips/TripsPanel';
import { TRIP_GROUPS, totalTrips, tripsBandCell, type TripGroupId, type TripGroups } from './trips/tripsModel';
import { useUserTrips, type TripGroupStatus } from './trips/useUserTrips';
import { userSectionAnchorId, type UserRecordData } from './views/userLayout';

/** The number keys after Trips (`1`): the cards in page order. */
const CARD_KEYS: UserRecordSection[] = ['contacts', 'reachability', 'identity', 'access', 'origin', 'technical'];

export interface ProfileTrips {
  /** The operator may list bookings and the user is not deleted. */
  enabled: boolean;
  groups: TripGroups;
  status: Record<TripGroupId, TripGroupStatus>;
  /** One "now" per visit: the axis, the groups' words and the band agree with each other. */
  now: Date;
  /** The person has bookings (the band's Trips cell links to the panel). */
  show: boolean;
  /** Trips are expected while the queries load (the user's trip count): the panel's
   *  skeleton takes that shape. */
  expected: boolean;
  cells: BandCell[];
}

/** The Trips queries and the account band's cells — shared by the band (above the ledger
 *  dock) and the Trips panel (in it). */
export function useProfileTrips(user: ConsoleUserDetail): ProfileTrips {
  const { hasPerm } = usePermissions();
  const enabled = hasPerm('console_bookings_view') && !user.is_deleted;
  const trips = useUserTrips(user.id, enabled);
  const [now] = useState(() => new Date());
  const failed = TRIP_GROUPS.some((g) => trips.status[g].error) && totalTrips(trips.groups) === null;
  const tripsCell = useMemo<BandCell | null>(() => {
    if (!enabled) return null;
    if (failed) return { id: 'trips', title: 'Trips', datum: 'Couldn’t load', detail: null };
    return tripsBandCell(trips.groups, user.kind, now);
  }, [enabled, failed, trips.groups, user.kind, now]);
  const cells = useMemo(() => accountBandCells(user, now, tripsCell), [user, now, tripsCell]);
  return {
    enabled,
    groups: trips.groups,
    status: trips.status,
    now,
    show: enabled && totalTrips(trips.groups) !== 0,
    expected: user.internal.trip_count > 0,
    cells,
  };
}

export function UserProfileBand({
  trips,
  tabs,
  hrefFor,
}: {
  trips: ProfileTrips;
  tabs: ReadonlySet<string>;
  hrefFor: (tab: BandTab) => string;
}) {
  return (
    <AccountBand
      cells={trips.cells}
      tabs={tabs}
      hrefFor={hrefFor}
      {...(trips.show ? { onTrips: () => focusAnchor(TRIPS_ANCHOR_ID) } : {})}
    />
  );
}

export interface UserProfileViewProps {
  trips: ProfileTrips;
  /** The editor's catalog, edit context and the detail; null while they load or failed. */
  data: UserRecordData | null;
  /** Why the editor's data could not load (null: loading or loaded). */
  dataError: unknown;
  onRetry: () => void;
  /** The page content's width (ledger column included); 0 = unmeasured. */
  width: number;
  /** Edit mode: the ledger docks beside the panels. */
  editing: boolean;
  tabs: ReadonlySet<string>;
  hrefFor: (tab: CardTab) => string;
}

export function UserProfileView({ trips, data, dataError, onRetry, width, editing, tabs, hrefFor }: UserProfileViewProps) {
  const { editWidth } = spineColumns(width);
  const tabHref = useCallback((tab: CardTab) => (tabs.has(tab) ? hrefFor(tab) : null), [tabs, hrefFor]);

  const onStage = useCallback(
    (i: number) => {
      if (i === 0) {
        if (trips.enabled) focusAnchor(TRIPS_ANCHOR_ID);
        return;
      }
      const section = CARD_KEYS[i - 1];
      if (section && data) focusAnchor(userSectionAnchorId(section));
    },
    [trips.enabled, data],
  );
  useRecordShortcuts({ onStage }, { stageCount: CARD_KEYS.length + 1 });

  return (
    <Box data-user-profile sx={{ display: 'grid', gap: 2 }}>
      {trips.enabled && (
        <TripsPanel
          groups={trips.groups}
          status={trips.status}
          bookingsHref={tabs.has('bookings') ? hrefFor('bookings') : null}
          now={trips.now}
          // Beside the docked ledger in edit mode; the panel's own width in view mode.
          layoutWidth={editing && width > 0 ? editWidth : 0}
          expectTrips={trips.expected}
        />
      )}
      {data ? (
        <UserCardsView data={data} width={width} tabHref={tabHref} />
      ) : dataError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Couldn’t load the person’s fields
          {dataError instanceof Error && dataError.message ? ` — ${dataError.message}` : ''}.
        </Alert>
      ) : (
        <Box role="status" aria-label="Loading the person’s fields" sx={{ display: 'grid', gap: 2 }}>
          <Skeleton variant="rounded" height={168} />
          <Skeleton variant="rounded" height={240} />
        </Box>
      )}
    </Box>
  );
}
